import React, { useEffect, useMemo, useState } from "react";
import "../../css/DesignSystem/Sidebar.css";
import { ReactComponent as Logo } from "../../assets/images/logo/RosemountLogoStaff.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../../backend/firebaseConfig";
import LogoutButton from "./Buttons/LogoutButton";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { ReactComponent as IconExpand } from "../../assets/icons/icon-Expand.svg";
import { ReactComponent as IconCollapse } from "../../assets/icons/icon-Collapse.svg";
import { ReactComponent as IconExit } from "../../assets/icons/Icon-Exit.svg";
import { ReactComponent as IconAllStaff } from "../../assets/icons/sidebar/AllStaff.svg";
import { ReactComponent as IconCollapsableAllStaff } from "../../assets/icons/sidebar/CollapseAllStaff.svg";
import { ReactComponent as IconAdviserSupport } from "../../assets/icons/sidebar/AdviserSupport.svg";
import { ReactComponent as IconCollapsableAdviserSupport } from "../../assets/icons/sidebar/CollapseAdviserSupport.svg";
import { ReactComponent as IconCompliance } from "../../assets/icons/sidebar/Compliance.svg";
import { ReactComponent as IconCollapsableCompliance } from "../../assets/icons/sidebar/CollapseCompliance.svg";
import { ReactComponent as IconMarketing} from "../../assets/icons/sidebar/Marketing.svg";
import { ReactComponent as IconCollapsableMarketing } from "../../assets/icons/sidebar/CollapseMarketing.svg";
import { ReactComponent as IconFinance } from "../../assets/icons/sidebar/Finance.svg";
import { ReactComponent as IconCollapsableFinance } from "../../assets/icons/sidebar/CollapseFinance.svg";
import { ReactComponent as IconAdministrator } from "../../assets/icons/sidebar/Administrator.svg";
import { ReactComponent as IconCollapsableAdministrator } from "../../assets/icons/sidebar/CollapseAdministrator.svg";
import { ReactComponent as IconParaplanning } from "../../assets/icons/sidebar/Paraplanning.svg";
import { ReactComponent as IconCollapsableParaplanning } from "../../assets/icons/sidebar/CollapseParaplanning.svg";
import { ReactComponent as IconAdviserPortal } from "../../assets/icons/sidebar/AdviserPortal.svg";
import { ReactComponent as IconCollapsableAdviserPortal } from "../../assets/icons/sidebar/CollapseAdviserPortal.svg";

import { getDepartmentConfig } from "../Departments/utils/departmentConfig";

const departmentIcons = {
  AllStaff: IconAllStaff,
  AdviserSupport: IconAdviserSupport,
  Compliance: IconCompliance,
  Intelliflo: IconCompliance,
  Marketing: IconMarketing,
  Finance: IconFinance,
  Administrator: IconAdministrator,
  Paraplanning: IconParaplanning, // Add the icon for Paraplanning
  AdviserPortal: IconAdviserPortal, // Add the icon for AdviserPortal
};

const collapsableDepartmentIcons = {
  AllStaff: IconCollapsableAllStaff,
  AdviserSupport: IconCollapsableAdviserSupport,
  Compliance: IconCollapsableCompliance,
  Intelliflo: IconCollapsableCompliance,
  Marketing: IconCollapsableMarketing,
  Finance: IconCollapsableFinance,
  Administrator: IconCollapsableAdministrator,
  Paraplanning: IconCollapsableParaplanning, // Add the collapsable icon for Paraplanning
  AdviserPortal: IconCollapsableAdviserPortal, // Add the collapsable icon for AdviserPortal  
};

const isDevelopmentEnvironment =
  process.env.REACT_APP_FIREBASE_ENV?.trim().toLowerCase() === "development";

const Sidebar = ({ isSetExpanded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userPermissions = [] } = useUserPermissions();
  const [activeIndex, setActiveIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(isSetExpanded);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        localStorage.removeItem("userPermissions");
        localStorage.removeItem("userEmail");
        navigate("/");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/Department/AllStaff", { replace: true });
    }
  }, [location.pathname, navigate]);

  const departmentItems = useMemo(() => {
    const configMap = getDepartmentConfig(userPermissions);

    return Object.entries(configMap)
      .filter(([, config]) => config.show)
      .map(([key, config]) => ({
        label: config.title,
        route: config.route || `/Department/${key}`,
        isExternal: /^https?:\/\//i.test(String(config.route || "")),
        internalRoute: `/Department/${key}`,
        icn: departmentIcons[key] || IconAllStaff,
        collapsableIcn:
          collapsableDepartmentIcons[key] || IconCollapsableAllStaff,
      }));
  }, [userPermissions]);

  useEffect(() => {
    const currentIndex = departmentItems.findIndex(
      (item) => !item.isExternal && item.internalRoute === location.pathname,
    );
    setActiveIndex(currentIndex >= 0 ? currentIndex : null);
  }, [location.pathname, departmentItems]);

  const handleItemClick = (item, index) => {
    if (item.isExternal) {
      window.open(item.route, "_blank", "noopener,noreferrer");
      return;
    }

    setActiveIndex(index);
    navigate(item.internalRoute);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return (
    <>
      <div className={`sidebar ${isExpanded ? "expanded" : ""}`}>
        <div className={`sidebar-content ${isExpanded ? "expanded" : ""}`}>
          <div className="sidebar-expanded">
            <IconExpand onClick={() => setIsExpanded((prev) => !prev)} />
          </div>

          <div className="expandIcon">
            <IconCollapse onClick={() => setIsExpanded((prev) => !prev)} />
          </div>

          <div className="sidebar-logo">
            <Logo
              onClick={() => {
                window.open("https://rosemountfs-ifa.co.uk/", "_blank");
              }}
            />
          </div>

          <div className="sidebar-content sidebar-scrollable-content">
            {departmentItems.map((item, index) => (
              <div
                className={`sidebar-Tab ${
                  !item.isExternal && location.pathname === item.internalRoute
                    ? "active"
                    : ""
                }`}
                key={item.route}
                onClick={() => handleItemClick(item, index)}
              >
                <div className="sidebar-defalut-icon">
                  <item.icn />
                </div>

                <div className="sidebar-responsive-icon">
                  <item.collapsableIcn />
                </div>

                <div
                  className={`Title ${
                    !item.isExternal && location.pathname === item.internalRoute
                      ? "active"
                      : ""
                  }`}
                >
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <IconExit className="exit-icon" onClick={handleLogout} />

        <div className="sidebar-footer">
          {isDevelopmentEnvironment && (
            <div className="sidebar-environment-badge Label">DEV</div>
          )}
          <div className="Content-Large">
            {auth.currentUser?.displayName || ""}
          </div>
          <LogoutButton title="Logout" onClick={handleLogout} />
        </div>
      </div>

      <div
        className={`sidebar-toggle ${isExpanded ? "open" : "closed"}`}
        aria-hidden={!isExpanded}
      >
        <div className="sidebar-toggle-column">
          <div>
            <IconCollapse
              onClick={() => setIsExpanded(false)}
              aria-label="Close navigation"
            />

            <div className="sidebar-toggle-logo">
              <Logo
                onClick={() => {
                  window.open("https://rosemountfs-ifa.co.uk/", "_blank");
                }}
              />
            </div>
          </div>

          {departmentItems.map((item, index) => (
            <div
              className={`sidebar-Tab ${
                !item.isExternal && location.pathname === item.internalRoute
                  ? "active"
                  : ""
              }`}
              key={`mobile-${item.route}`}
              onClick={() => handleItemClick(item, index)}
            >
              <div className="sidebar-toggle-icon">
                <item.icn />
              </div>

              <div className="Title">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="sidebar-toggle-footer">
          {isDevelopmentEnvironment && (
            <div className="sidebar-environment-badge Label">DEV</div>
          )}
          <div className="Label">
            {auth.currentUser?.displayName || ""}
          </div>
          <LogoutButton title="Logout" onClick={handleLogout} />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
